<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<template>
  <div class="w-full flex flex-col relative">
    <div
      v-if="position === 'top'"
      class="flex items-center h-14 w-full bg-white/90 px-standard text-neutral-800 backdrop-blur-sm hover:underline overflow-y-scroll scrollbar-thin scrollbar-thumb-neutral-200 scrollbar-track-transparent"
      @click="topNavOpen = !topNavOpen"
    >
      <div class="flex-1 items-center flex justify-between cursor-pointer">
        <div class="text-left text-ocean-900 font-semibold line-clamp-1 text-ellipsis">
          {{ activeNavLabel }}
        </div>
        <div>
          <fa
            :icon="['fas', topNavOpen ? 'times' : 'angle-down']"
            class="text-base transition fa-fw"
            :class="{
              'rotate-180': topNavOpen,
            }"
          />
        </div>
      </div>
    </div>
    <div
      id="nav-container"
      class="w-full"
      :class="{
        'absolute top-14 overflow-x-hidden overflow-y-scroll scrollbar-thin scrollbar-thumb-neutral-200 scrollbar-track-transparent scrollbar-thumb-rounded-full backdrop-blur-sm bg-white/90 pb-4 ': position === 'top',
        hidden: position === 'top' && !topNavOpen,
        'h-[calc(100vh-3.5rem)]': isWebView,
        'h-[calc(100vh-3.5rem-4rem)]': !isWebView,
      }"
    >
      <button
        v-if="position === 'side'"
        type="button"
        class="flex rounded-md bg-ocean-100 hover:bg-white border border-ocean-200 w-6 aspect-1 items-center justify-center"
        aria-label="Toggle table of contents visibility"
        @click="toggleStickyOpen"
      >
        <fa
          :icon="['fal', stickyOpen ? 'angle-right' : 'list']"
          :class="{ 'rotate-180': stickyOpen }"
          class="text-ocean-900 transition-all duration-150"
        />
      </button>
      <div class="w-full">
        <div
          v-if="linkType === 'route'"
          class="flex w-full flex-col space-y-1 lg:space-y-2 mt-0 lg:mt-4"
        >
          <vetster-dynamic-sticky-nav-link
            v-for="category in categories"
            :key="getCategoryId(category)"
            :sticky-open="stickyOpen"
            :active-nav="activeNav"
            :href="getCategoryUrl(category)"
            :icon="get(category, 'icon') || 'file-lines'"
            :label="getCategoryLabel(category)"
            :nav-id="getCategoryId(category)"
            :category="category"
            :children="category?.content"
            :position="position"
            :link-type="linkType"
            @click="handleOnClick"
          />
        </div>
        <div
          v-else
          class="flex w-full flex-col space-y-1 lg:space-y-2 mt-0 lg:mt-4"
        >
          <vetster-dynamic-sticky-nav-link
            v-for="category in categories"
            :key="getCategoryId(category)"
            :sticky-open="stickyOpen"
            :active-nav="activeNav"
            :href="getCategoryUrl(category)"
            :icon="getIconName(get(category, 'slug')) ? getIconName(get(category, 'slug')) : 'angle-right'"
            :label="getCategoryLabel(category)"
            :nav-id="getCategoryId(category)"
            :position="position"
            :link-type="linkType"
            @click="handleOnClick"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faList } from '@fortawesome/pro-light-svg-icons';
import get from 'lodash/get';

import VetsterDynamicStickyNavLink from '@/components/VetsterDynamicStickyNavLink';
import { slugCase, snakeCase, wrapHash } from '@/helpers/format';
import CloseOnEscape from '@/mixins/CloseOnEscape';
import HasNavMenu from '@/mixins/HasNavMenu';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';

library.add(faList);

const Icons = {
  'new-pet': 'sparkles',
  safety: 'shield',
  travel: 'route',
  gifts: 'gift',
  stress: 'siren-on',
  'key-takeaways': 'key',
  'a-closer-look': 'file-search',
  'connect-with-a-vet': 'user-md',
  'risk-factors': 'exclamation-triangle',
  'possible-causes': 'file-medical',
  references: 'books',
  'testing-and-diagnosis': 'stethoscope',
  'similar-symptoms': 'file-medical',
  'associated-symptoms': 'file-medical',
  'editorial-committee': 'users',
  'more-information': 'info-circle',
  dog: 'dog',
  cat: 'cat',
  horse: 'horse',
  turtle: 'turtle',
  bird: 'bird',
  'avoid-cat-astrophes': 'sparkles',
  'book-an-online-vet': 'sparkles',
  top: 'arrow-up-to-line',
  survey: 'square-poll-vertical',
  articles: 'newspaper',
  next_steps: 'signs-post',
  'what-is-drug': 'prescription',
  'how-it-works': 'file-prescription',
  'how-is-it-given': 'file-prescription',
  'side-effects': 'exclamation-triangle',
  'safety-overdose': 'hand-holding-medical',
  'missed-dosage': 'prescription-bottle-medical',
  'drug-interaction': 'file-medical',
  'alternatives-to-drug': 'prescription-bottle-pill',
  'associated-conditons': 'stethoscope',
  'medications-and-treatments': 'prescription-bottle-pill',
  'conditions-to-manage': 'stethoscope',
  'concerned-about-a-symptom': 'stethoscope',
  'what-can-and-cannot-eat': 'clipboard-question',
  'what-is-the-veterinarian-role': 'user-doctor',
  'how-to-keep-your-companion-healthy': 'hand-holding-medical',
  'dog-breeds': 'dog',
  'symptoms-conditions-behavior': 'stethoscope',
  'history-of-breed': 'book-medical',
  'quick-facts': 'file-magnifying-glass',
  'are-breed-right-for-you': 'clipboard-question',
};

export default {
  name: 'VetsterDynamicStickyNav',

  components: { VetsterDynamicStickyNavLink },

  mixins: [
    CloseOnEscape,
    HasNavMenu,
    LocalizesRoutes,
  ],

  props: {
    appendId: {
      type: String,
      default: '',
    },
    categories: {
      type: Array,
      default: () => [],
    },
    position: {
      type: String,
      default: 'side',
    },
    default: {
      type: String,
      default: 'Choose a section...',
    },
    isWebView: {
      type: Boolean,
      default: false,
    },
    // Note: linkType can be `route` (a fully qualified URL), `url` (requires assembling the URL based on paths and parts) or `anchor` (an anchor within a page)
    linkType: {
      type: String,
      default: 'anchor',
    },
    linkPath: {
      type: String,
      default: '',
    },
    linkCategory: {
      type: String,
      default: '',
    },
    linkSlug: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      topNavOpen: false,
      selectedCategory: {},
    };
  },

  computed: {
    stickyOpen() {
      return this.$layout.stickyNavOpen;
    },
    activeNavLabel() {
      if (!this.activeNav) {
        return this.default;
      }

      return this.getLabelFromAnchor(this.activeNav);
    },
    topOffset() {
      // TODO: Verify - this appears to be off a little
      // TODO: This could be dynamically calculated based on the dom height of the top nav
      // Mobile main nav and y padding 85px (w-16) + top nav 56px (w-14)
      // Desktop main nav and y padding 85px (w-16)
      const topOffset = this.isWebView ? 85 : 85 + 56;

      return this.position === 'top' ? topOffset : 85;
    },
  },

  created() {
    if (this.linkCategory) {
      this.activeNav = wrapHash(this.linkCategory);
      const value = this.categories.find((category) => get(category, 'slug') === this.linkCategory);
      if (value) {
        this.selectedCategory = value;
      }
    }
  },

  mounted() {
    // Remove width classes from the TOC that were used to maintain space before vue is loaded
    this.resizeHandler();

    // Register Close on Escape
    this.closeOnEscape(this.closeTopNav);

    const element = document.getElementById('table-of-contents');
    element.classList.remove('w-16');
    element.classList.remove('xl:w-64');
    element.classList.remove('2xl:w-80');

    document.addEventListener('scroll', this.scrollHandler);
    window.addEventListener('resize', this.resizeHandler);
  },

  beforeDestroy() {
    document.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
    this.destroyCloseOnEscape();
  },

  methods: {
    closeTopNav() {
      if (this.position === 'top') {
        this.topNavOpen = false;
      }
    },
    getCategoryId(category) {
      return snakeCase(`${get(category, this.titleKey)}${this.appendId}`);
    },
    getCategoryLabel(category) {
      return this.getLabel(get(category, this.titleKey));
    },
    getCategoryUrl(category) {
      switch (this.linkType) {
        case 'anchor':
          return wrapHash(get(category, this.titleKey));
        case 'route':
          return get(category, 'route');
        default:
          return this.generateRedirectUrlForNavId(get(category, 'slug'));
      }
    },
    getIconName(key) {
      return Icons[key] || '';
    },
    handleOnClick(idOrSlug) {
      if (this.linkType === 'anchor' && idOrSlug?.navId) {
        this.navigateToSection(slugCase(idOrSlug?.navId));
      } else if (idOrSlug?.route) {
        this.redirect(idOrSlug?.route);
      } else if (idOrSlug?.navId) {
        this.redirect(this.generateRedirectUrlForNavId(idOrSlug?.navId));
      }
    },
    navigateToSection(id) {
      if (this.position === 'top') {
        this.topNavOpen = false;
      }
      try {
        window.scrollTo({
          top: document.getElementById(id).getBoundingClientRect().top + window.scrollY - this.topOffset,
          behavior: 'smooth',
        });
      } catch (e) { /* empty */ }
    },
    toggleStickyOpen() {
      this.$layout.toggleStickyNav();
    },
    // TODO: The redirect and generateRedirectUrlForNavId behavior is a bit convoluted - need to spend some time to clean the logic up.
    // TODO: NavLink is recursive, sometimes links anchor, sometimes link to a URL, sometimes to a locale base, sometimes to a language base
    // TODO: NavLink also propagates events up through recursion to Nav which could contain either a route or a navId
    redirect(url) {
      window.location.href = url;
    },
    generateRedirectUrlForNavId(slug) {
      if (this.linkType === 'route') {
        return slug;
      }

      return [
        [
          this.localizeLanguageUrl(`${this.linkPath}/${slug}`),
          this.linkSlug,
        ].filter(Boolean).join('/'),
        this.params,
      ].filter(Boolean).join('?');
    },
    resizeHandler() {
      if (this.position === 'top') {
        this.$layout.openStickyNav();

        return;
      }
      if (!this.$screen('lg') || this.$screen('xl')) {
        this.$layout.openStickyNav();
      } else if (!this.$screen('xl')) {
        this.$layout.closeStickyNav();
      }
    },
    scrollHandler() {
      if (this.linkType === 'anchor') {
        document.querySelectorAll('.post').forEach((el) => {
          const condition = el.getBoundingClientRect().top < 150
            && el.getBoundingClientRect().top >= -el.getBoundingClientRect().height;

          if (condition) {
            this.activeNav = wrapHash(el.id);
          }
        });
      }
    },
    get,
    snakeCase,
    slugCase,
    wrapHash,
  },
};
</script>
